import { useEffect, useState } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * Dark-blue technical auth shell shared across Login / Register /
 * ForgotPassword / ResetPassword pages.
 *
 * Layout:
 *   - Full-screen deep navy background with subtle grid + radial glow
 *   - Top-left brand mark + step indicator
 *   - Centered narrow card (max-w-md) with the form
 *   - Bottom status bar (system online / region / version)
 */
export default function AuthShell({ step, title, subtitle, children }) {
  const [clock, setClock] = useState("");
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mm = String(d.getUTCMinutes()).padStart(2, "0");
      const ss = String(d.getUTCSeconds()).padStart(2, "0");
      setClock(`${hh}:${mm}:${ss} UTC`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050B1F] text-white font-geist">
      {/* Background layers */}
      <div className="absolute inset-0 auth-grid pointer-events-none" />
      <div className="absolute inset-0 auth-glow pointer-events-none" />
      <div className="absolute inset-0 auth-noise pointer-events-none opacity-40" />

      {/* Top brand bar */}
      <header className="relative flex items-center justify-between px-6 md:px-10 py-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-4">
          <div className="h-9 w-9 rounded-sm bg-gradient-to-br from-[#4D8AFF] to-[#002FA7] flex items-center justify-center shadow-[0_0_28px_-6px_rgba(77,138,255,0.6)]">
            <span className="font-geist-mono text-[15px] font-bold text-white">R</span>
          </div>
          <div className="flex flex-col leading-tight">
            <div className="font-geist text-[18px] font-semibold text-white tracking-tight">
              Revo <span className="text-[#4D8AFF]">PDF</span> Loader
            </div>
            <div className="font-geist-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
              by FudoSoftware
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <div className="hidden md:flex items-center gap-6 font-geist-mono text-[10px] uppercase tracking-wider text-white/40">
            <span>{clock}</span>
            <span>build 2.1.0</span>
          </div>
        </div>
      </header>

      {/* Main center */}
      <main className="relative flex items-center justify-center px-6 py-10 md:py-16 min-h-[calc(100vh-140px)]">
        <div className="w-full max-w-[440px]">
          {/* Step chip */}
          <div className="flex items-center gap-2 mb-8">
            <div className="h-1.5 w-1.5 rounded-full bg-[#4D8AFF] animate-pulse" />
            <span className="font-geist-mono text-[10px] uppercase tracking-[0.2em] text-[#7B8FB8]">
              {step}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-geist text-4xl sm:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-[15px] text-white/60 leading-relaxed max-w-[400px]">
              {subtitle}
            </p>
          )}

          {/* Card */}
          <div className="mt-10 rounded-md border border-white/[0.08] bg-[#0A1738]/60 backdrop-blur-md p-7 shadow-[0_0_60px_-20px_rgba(77,138,255,0.35)]">
            {children}
          </div>
        </div>
      </main>

      {/* Bottom status bar */}
      <footer className="relative border-t border-white/[0.06] px-6 md:px-10 py-3 flex items-center justify-between font-geist-mono text-[10px] uppercase tracking-wider text-white/40">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            system online
          </span>
          <span className="hidden sm:inline">region · eu-west</span>
          <span className="hidden md:inline">tls 1.3</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline">© 2026 FudoSoftware</span>
          <span className="hidden md:inline">·</span>
          <span>encrypted end-to-end</span>
        </div>
      </footer>
    </div>
  );
}
